import { NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Lightbulb,
  Settings,
  ChevronLeft,
  ChevronRight,
  Zap,
  Building2,
  TrendingUp,
  Trophy,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Decisions', href: '/decisions', icon: Lightbulb },
]

const verticals = [
  { name: 'Enterprise Ops', href: '/verticals/ops', icon: Building2 },
  { name: 'Marketing', href: '/verticals/marketing', icon: TrendingUp },
  { name: 'Sports', href: '/verticals/sports', icon: Trophy },
]

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation()

  return (
    <>
      {/* Mobile overlay */}
      <div className="fixed inset-0 z-40 lg:hidden" />

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50',
          'flex flex-col bg-primary-900',
          'transition-all duration-300 ease-out',
          'hidden lg:flex',
          collapsed ? 'w-20' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-primary-800">
          <NavLink to="/dashboard" className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-accent-500 shadow-glow-accent">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="font-display text-lg font-bold text-white"
                >
                  DecisionFlow
                </motion.span>
              )}
            </AnimatePresence>
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-6">
          {/* Main navigation */}
          <div className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/dashboard' && location.pathname.startsWith(item.href))

              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-xl',
                    'text-sm font-medium transition-all duration-200',
                    'group relative',
                    isActive
                      ? 'bg-accent-500/20 text-white'
                      : 'text-primary-200 hover:bg-primary-800 hover:text-white'
                  )}
                >
                  {isActive && (
                    <motion.div
                      layoutId="sidebar-active"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-500 rounded-r-full"
                    />
                  )}
                  <item.icon className={cn(
                    'w-5 h-5 flex-shrink-0',
                    isActive ? 'text-accent-400' : 'text-primary-400 group-hover:text-white'
                  )} />
                  <AnimatePresence>
                    {!collapsed && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </NavLink>
              )
            })}
          </div>

          {/* Verticals section */}
          <div className="space-y-1">
            <AnimatePresence>
              {!collapsed && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="px-3 text-xs font-semibold text-primary-500 uppercase tracking-wider"
                >
                  Verticals
                </motion.p>
              )}
            </AnimatePresence>
            <div className="mt-2 space-y-1">
              {verticals.map((item) => {
                const isActive = location.pathname.startsWith(item.href)

                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-xl',
                      'text-sm font-medium transition-all duration-200',
                      'group',
                      isActive
                        ? 'bg-accent-500/20 text-white'
                        : 'text-primary-200 hover:bg-primary-800 hover:text-white'
                    )}
                  >
                    <item.icon className={cn(
                      'w-5 h-5 flex-shrink-0',
                      isActive ? 'text-accent-400' : 'text-primary-400 group-hover:text-white'
                    )} />
                    <AnimatePresence>
                      {!collapsed && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          {item.name}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                )
              })}
            </div>
          </div>
        </nav>

        {/* Settings & Collapse */}
        <div className="border-t border-primary-800 p-3 space-y-1">
          <NavLink
            to="/settings"
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-xl',
              'text-sm font-medium transition-all duration-200',
              'text-primary-200 hover:bg-primary-800 hover:text-white',
              location.pathname.startsWith('/settings') && 'bg-primary-800 text-white'
            )}
          >
            <Settings className="w-5 h-5 flex-shrink-0 text-primary-400" />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Settings
                </motion.span>
              )}
            </AnimatePresence>
          </NavLink>

          <button
            onClick={onToggle}
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-xl w-full',
              'text-sm font-medium transition-all duration-200',
              'text-primary-400 hover:bg-primary-800 hover:text-white'
            )}
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  )
}
